/**
=========================================================
* Material Dashboard 2 React - v2.2.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useEffect, useRef, useState } from "react";

// @mui material components
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDAlert from "components/MDAlert";
import MDButton from "components/MDButton";
import MDSnackbar from "components/MDSnackbar";

// Material Dashboard 2 React example components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import Table from "@mui/material/Table";
import DataTable from "../../../examples/Tables/DataTable";
import data from "../../dashboard/components/Projects/data";
import useApi from "../../../components/Utils/api";
import MDInput from "../../../components/MDInput";
import toast from "react-hot-toast";
import subDays from "date-fns/subDays";
import startOfMonth from "date-fns/startOfMonth";
import addMonths from "date-fns/addMonths";
import endOfMonth from "date-fns/endOfMonth";
import { DateRangePicker } from "rsuite";
import { afterToday } from "rsuite/cjs/DateRangePicker/disabledDateUtils";
import reportsLineChartData from "../../dashboard/data/reportsLineChartData";
import Icon from "@mui/material/Icon";

import Papa from 'papaparse';
function MerchantStatement() {
  const { merchantStatementColumns } = data();
  const { predefinedRanges } = reportsLineChartData;
  const [summary, setSummary] = useState([]);
  const getTodayDateAtMidnight = () => {
    const now = new Date();
    now.setHours(0, 0, 0, 0); // Set to start of the day
    const offset = now.getTimezoneOffset(); // Get timezone offset in minutes
    now.setMinutes(now.getMinutes() - offset); // Adjust by offset to get local time
    return now;
  };

  // Initialize lastMonthDateRange to today's date at local midnight
  const [lastMonthDateRange, setLastMonthDateRange] = useState([
    getTodayDateAtMidnight().toISOString().slice(0, 10),
    getTodayDateAtMidnight().toISOString().slice(0, 10)
  ]);
  const api = useApi();
  const formatDate = (date) => {
    const isoDate = new Date(date.toLocaleString("en-US", { timeZone: "Australia/Sydney" })).toISOString();
    return isoDate.slice(0, 10);
  };
  const getSettlementReport = (lastMonthDateRange) => {
    if(lastMonthDateRange == null) return;
    setLastMonthDateRange(lastMonthDateRange);
    const data = {
      fromDate : formatDate(lastMonthDateRange[0]),
      toDate : formatDate(lastMonthDateRange[1]),
    }
    api.get("merchant-statement/list?startDate="+formatDate(lastMonthDateRange[0])+"&endDate="+formatDate(lastMonthDateRange[1]))
      .then((response) => {
        if(response.data.statusCodeValue === 200) {
          const modifiedData = response.data.body.map(item => {
            const action = (
                <MDBox display="flex" alignItems="center" lineHeight={1}>
                  <Icon
                      fontSize="small"
                      style={{cursor : 'pointer'}}
                      onClick={() => {
                        const downloadUrl = process.env.REACT_APP_BASE_URL+"/api/adminPortal/v1/merchant-statement/download/"+item.id;

                        // Open a new tab and redirect to the download URL
                        window.open(downloadUrl, '_blank');
                      }}
                  >download</Icon>
                </MDBox>
            );
            return { ...item, action };
          });
          setSummary(modifiedData);
        }
        else{

          const toastId = toast.error("No Record Found", { autoClose: false }); // Prevents auto-closing
          setTimeout(() => toast.dismiss(toastId), 1000); // Dismisses the toast after 1 second
        }
      })
      .catch((error) => {
          if (error.response && error.response.status === 403) {
              toast.error("You do not have permission to perform this action.");
              return;
          }
          const toastId = toast.error("No Record Found", { autoClose: false }); // Prevents auto-closing
          setTimeout(() => toast.dismiss(toastId), 1000); // Dismisses the toast after 1 second
      })
  }
  const generateCSV = () => {
    if (summary.length === 0) {
      return '';
    }
  
    const headers = merchantStatementColumns
      .filter(column => column.Header !== "Action") // Exclude "Action" column
      .map(column => column.Header);
  
    const summaryWithoutAction = summary.map(item => {
      return merchantStatementColumns
        .filter(column => column.Header !== "Action") // Exclude "Action" column
        .map(column => {
          const accessor = column.accessor;
          const value = typeof accessor === 'function' ? accessor(item) : item[accessor];
          return value;
        });
    });
  
    const csvData = [headers, ...summaryWithoutAction];
  
    const csv = Papa.unparse(csvData, {
      quotes: true,
    });
  
    return csv;
  };
  

  const handleDownloadCSV = () => {
    if (summary.length === 0) {
      const toastId = toast.error("No records found to download", { autoClose: false }); // Prevents auto-closing
      setTimeout(() => toast.dismiss(toastId), 1000); // Dismisses the toast after 1 second
      
      return;
    }

    const csvData = generateCSV();

    const currentDate = new Date().toISOString().slice(0, 10);
    const currentTime = new Date().toLocaleTimeString().replace(/:/g, '-');
    const dateTimeString = `${currentDate}_${currentTime}`;

    const fileName = `MerchantStatement_${dateTimeString}.csv`;

    const blob = new Blob([csvData], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  useEffect(() => {
    if(lastMonthDateRange !== null){
      getSettlementReport(lastMonthDateRange);
    }
  },[lastMonthDateRange]);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox mt={6} mb={3}>
        <Grid container spacing={3} justifyContent="center">
          <Grid item xs={12} lg={12}>
            <Grid item xs={12} md={12} lg={12}>
              <MDBox mb={3} textAlign={"center"}>
                <DateRangePicker name="date-range" disabledDate={afterToday()} ranges={predefinedRanges} onChange={(date) => getSettlementReport(date)} format="yyyy-MM-dd" value={Object.keys(lastMonthDateRange).length !== 0 ? [new Date(lastMonthDateRange[0]), new Date(lastMonthDateRange[1])] : []} />
              </MDBox>
            </Grid>
            <MDButton
              variant="gradient"
              onClick={handleDownloadCSV}
              color="info"
              style={{ marginRight: "24px", marginBottom: "12px" }}
            >Export</MDButton>
            <DataTable
              table={{ columns : merchantStatementColumns, rows : summary }}
              showTotalEntries={true}
              isSorted={true}
              noEndBorder
              entriesPerPage={true}
              canSearch={true}
            />
          </Grid>
        </Grid>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default MerchantStatement;
